import * as React from "react";

import {
  Children,
  cloneElement,
  isValidElement,
  useRef,
  useState,
} from "react";
import { ValidationError, useTranslate } from "ra-core";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import AddIcon from "@material-ui/icons/AddCircleOutline";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import Button from "@material-ui/core/Button";
import CloseIcon from "@material-ui/icons/RemoveCircleOutline";
import Collapse from "@material-ui/core/Collapse";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import FormHelperText from "@material-ui/core/FormHelperText";
import { FormInput } from "react-admin";
import IconButton from "@material-ui/core/IconButton";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";
import PropTypes from "prop-types";
import TablePagination from "@material-ui/core/TablePagination";
import Typography from "@material-ui/core/Typography";
import classNames from "classnames";
import get from "lodash/get";
import { useForm } from "react-final-form";

const useStyles1 = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(0),
  },
}));

function TablePaginationActions(props) {
  const classes = useStyles1();
  const theme = useTheme();
  const { count, page, rowsPerPage, onChangePage } = props;

  const handleFirstPageButtonClick = (event) => {
    onChangePage(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onChangePage(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onChangePage(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}
const useStyles = makeStyles(
  (theme) => ({
    root: {
      padding: 0,
      marginBottom: 0,
      "& > li:last-child": {
        borderBottom: "none",
      },
    },
    line: {
      display: "flex",
      listStyleType: "none",
      borderBottom: `solid 1px ${theme.palette.divider}`,
      [theme.breakpoints.down("xs")]: { display: "block" },
      "&.fade-enter": {
        opacity: 0.01,
        transform: "translateX(100vw)",
      },
      "&.fade-enter-active": {
        opacity: 1,
        transform: "translateX(0)",
        transition: "all 500ms ease-in",
      },
      "&.fade-exit": {
        opacity: 1,
        transform: "translateX(0)",
      },
      "&.fade-exit-active": {
        opacity: 0.01,
        transform: "translateX(100vw)",
        transition: "all 500ms ease-in",
      },
    },

    lineHidden: {
      display: "none",
    },
    index: {
      width: "3em",
      paddingTop: "1em",
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    form: { flex: 2 },
    action: {
      paddingTop: "0.5em",
    },
    leftIcon: {
      marginRight: theme.spacing(1),
    },
    collapse: {
      display: "inline",
      clear: "none",
    },
    collapseBlock: {
      display: "block",
      clear: "both",
    },
    collapseTitle: {
      margin: theme.spacing(1),
      cursor: "pointer",
      display: "inline-block",
    },
    sortableWrapper: {
      marginTop: theme.spacing(1),
      float: "right",
      display: "block",
      "& button": {
        marginRight: theme.spacing(1),
      },
      "& svg": {
        width: 32,
        height: 32,
      },
    },
  }),
  { name: "RaSimpleFormIterator" }
);

const DefaultAddButton = (props) => {
  const classes = useStyles(props);
  const translate = useTranslate();
  return (
    <Button size="small" {...props}>
      <AddIcon className={classes.leftIcon} />
      {translate("ra.action.add")}
    </Button>
  );
};

const DefaultRemoveButton = (props) => {
  const classes = useStyles(props);
  const translate = useTranslate();
  return (
    <Button size="small" {...props}>
      <CloseIcon className={classes.leftIcon} />
      {translate("ra.action.remove")}
    </Button>
  );
};

const SimpleFormIterator = (props) => {
  const {
    perPage = 1,
    addButton = <DefaultAddButton />,
    removeButton = <DefaultRemoveButton />,
    basePath,
    children,
    fields,
    meta: { error, submitFailed },
    record,
    resource,
    source,
    disableAdd,
    disableRemove,
    variant,
    margin,
    header,
    sortable,
    defaultValue,
    collapsable,
    listIndexRenderer = null,
    showListIndex = true,
    showPagination = true,
  } = props;
  const form = useForm();
  const classes = useStyles(props);
  const translate = useTranslate();
  const [expanded, setExpanded] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: perPage,
    count: fields.length,
  });
  // We need a unique id for each field for a proper enter/exit animation
  // so we keep an internal map between the field position and an auto-increment id
  const nextId = useRef(
    fields && fields.length
      ? fields.length
      : defaultValue
      ? defaultValue.length
      : 0
  );

  // We check whether we have a defaultValue (which must be an array) before checking
  // the fields prop which will always be empty for a new record.
  // Without it, our ids wouldn't match the default value and we would get key warnings
  // on the CssTransition element inside our render method
  const ids = useRef(
    nextId.current > 0 ? Array.from(Array(nextId.current).keys()) : []
  );

  const refreshOrder = (removing) => {
    const length = removing ? fields.length - 1 : fields.length + 1;
    for (var idx = 0; idx < length; idx++) {
      form.change(`${props.source}[${idx}].order_index`, idx);
    }
  };

  const removeField = (index) => () => {
    const count = pagination.count - 1;
    const totalPages =
      Math.floor(count / pagination.rowsPerPage) +
      (count % pagination.rowsPerPage !== 0 ? 1 : 0);
    let page = pagination.page;
    if (page >= totalPages) {
      page = page - 1;
    }
    if (page < 0) {
      page = 0;
    }

    setPagination({
      ...pagination,
      count,
      page,
    });
    ids.current.splice(index, 1);
    fields.remove(index);
    setTimeout(() => {
      refreshOrder(true);
    }, 1);
  };

  // Returns a boolean to indicate whether to disable the remove button for certain fields.
  // If disableRemove is a function, then call the function with the current record to
  // determining if the button should be disabled. Otherwise, use a boolean property that
  // enables or disables the button for all of the fields.
  const disableRemoveField = (record, disableRemove) => {
    if (typeof disableRemove === "boolean") {
      return disableRemove;
    }

    return disableRemove && disableRemove(record);
  };

  const addField = () => {
    const lastIndex = nextId.current++;
    ids.current.push(lastIndex);
    const count = pagination.count + 1;
    const page =
      Math.floor(count / pagination.rowsPerPage) +
      (count % pagination.rowsPerPage !== 0 ? 1 : 0) -
      1;

    setPagination({ ...pagination, count, page });
    setExpanded(expanded.concat([lastIndex]));
    fields.push({});
    setTimeout(refreshOrder, 1000);
  };

  const handleMoveUp = (index, evt) => {
    evt.stopPropagation();
    evt.preventDefault();

    form.change(`${props.source}[${index}].order_index`, index - 1 + 1);
    form.change(`${props.source}[${index - 1}].order_index`, index + 1);
    fields.move(index, index - 1);

    setTimeout(refreshOrder, 1);
  };
  const handleMoveDown = (index, evt) => {
    evt.stopPropagation();
    evt.preventDefault();

    form.change(`${props.source}[${index}].order_index`, index + 1 + 1);
    form.change(`${props.source}[${index + 1}].order_index`, index + 1);
    fields.move(index, index + 1);

    setTimeout(refreshOrder, 1);
  };

  // add field and call the onClick event of the button passed as addButton prop
  const handleAddButtonClick = (originalOnClickHandler) => (event) => {
    addField();
    if (originalOnClickHandler) {
      originalOnClickHandler(event);
    }
  };

  // remove field and call the onClick event of the button passed as removeButton prop
  const handleRemoveButtonClick =
    (originalOnClickHandler, index) => (event) => {
      removeField(index)();
      if (originalOnClickHandler) {
        originalOnClickHandler(event);
      }
    };

  const handleRowToggle = (index) => {
    if (expanded.indexOf(index) !== -1) {
      setExpanded(expanded.filter((e) => e !== index));
    } else {
      setExpanded(expanded.concat([index]));
    }
  };

  const handlePageChange = (_, newPage) =>
    setPagination({ ...pagination, page: newPage });
  const handleChangeRowsPerPage = (evt) =>
    setPagination({
      ...pagination,
      page: 0,
      rowsPerPage: parseInt(evt.target.value, 10),
    });

  const records = get(record, source);
  const lowerBound = pagination.rowsPerPage * pagination.page;
  const upperBound =
    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage;

  const isRowVisible = (index) => index >= lowerBound && index < upperBound;
  return fields ? (
    <>
      <ul className={classes.root}>
        {submitFailed && typeof error !== "object" && error && (
          <FormHelperText error>
            <ValidationError error={error} />
          </FormHelperText>
        )}
        {fields.map((member, index) => (
          <li
            key={ids.current[index] || index}
            className={classNames(
              classes.line,
              !isRowVisible(index) && classes.lineHidden
            )}
          >
            {showListIndex && (
              <Typography variant="body1" className={classes.index}>
                {listIndexRenderer
                  ? listIndexRenderer(index, member)
                  : index + 1}
              </Typography>
            )}

            <section className={classes.form}>
              {collapsable && (
                <div
                  onClick={handleRowToggle.bind(this, index)}
                  className={classes.collapse}
                >
                  {typeof header === "string" && (
                    <Typography variant="h5" className={classes.collapseTitle}>
                      {get(
                        fields.value,
                        `[${index}].${header}`,
                        translate(
                          `app.list_row.${
                            !collapsable || expanded.indexOf(index) !== -1
                              ? "hide"
                              : "show"
                          }`
                        )
                      )}
                    </Typography>
                  )}
                  {typeof header === "function" && (
                    <Typography variant="h5" className={classes.collapseTitle}>
                      {header(get(fields.value, `[${index}]`))}
                    </Typography>
                  )}
                  {!header && (
                    <Typography variant="h5" className={classes.collapseTitle}>
                      {translate("app.list_row.index", { index: index + 1 })}
                    </Typography>
                  )}
                  {sortable && (
                    <div className={classes.sortableWrapper}>
                      <Button
                        variant="contained"
                        disableElevation
                        disabled={index === 0}
                        title={translate("app.move_up")}
                        onClick={handleMoveUp.bind(this, index)}
                      >
                        <ArrowDropUpIcon />
                      </Button>

                      <Button
                        variant="contained"
                        disableElevation
                        disabled={index === fields.length - 1}
                        title={translate("app.move_down")}
                        onClick={handleMoveDown.bind(this, index)}
                      >
                        <ArrowDropDownIcon />
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <Collapse
                className={classes.collapseBlock}
                in={!collapsable || expanded.indexOf(index) !== -1}
                timeout="auto"
              >
                {Children.map(children, (input, index2) =>
                  isValidElement(input) ? (
                    <FormInput
                      basePath={input.props.basePath || basePath}
                      input={cloneElement(input, {
                        source: input.props.source
                          ? `${member}.${input.props.source}`
                          : member,
                        index: input.props.source ? undefined : index2,
                        label:
                          typeof input.props.label === "undefined"
                            ? input.props.source
                              ? `resources.${resource}.fields.${input.props.source}`
                              : undefined
                            : input.props.label,
                      })}
                      record={records && records[index]}
                      resource={resource}
                      variant={variant}
                      margin={margin}
                    />
                  ) : null
                )}
              </Collapse>
            </section>

            {!disableRemoveField(
              (records && records[index]) || {},
              disableRemove
            ) && (
              <span className={classes.action}>
                {cloneElement(removeButton, {
                  onClick: handleRemoveButtonClick(
                    removeButton.props.onClick,
                    index
                  ),
                  className: classNames(
                    "button-remove",
                    `button-remove-${source}-${index}`
                  ),
                })}
              </span>
            )}
          </li>
        ))}
        {!disableAdd && (
          <li className={classes.line}>
            <span className={classes.action}>
              {cloneElement(addButton, {
                onClick: handleAddButtonClick(addButton.props.onClick),
                className: classNames("button-add", `button-add-${source}`),
              })}
            </span>
          </li>
        )}
      </ul>
      {showPagination && pagination.count > 1 && (
        <TablePagination
          component="div"
          count={pagination.count}
          page={pagination.page}
          onChangePage={handlePageChange}
          rowsPerPage={pagination.rowsPerPage}
          rowsPerPageOptions={[perPage, perPage * 2, perPage * 4, perPage * 10]}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          SelectProps={{
            inputProps: { "aria-label": translate("app.rows_per_page") },
          }}
          ActionsComponent={TablePaginationActions}
        />
      )}
    </>
  ) : null;
};

SimpleFormIterator.defaultProps = {
  disableAdd: false,
  disableRemove: false,
  collapsable: false,
  perPage: 10,
};

SimpleFormIterator.propTypes = {
  defaultValue: PropTypes.any,
  addButton: PropTypes.element,
  removeButton: PropTypes.element,
  basePath: PropTypes.string,
  children: PropTypes.node,
  classes: PropTypes.object,
  className: PropTypes.string,
  // @ts-ignore
  fields: PropTypes.object,
  meta: PropTypes.object,
  // @ts-ignore
  record: PropTypes.object,
  source: PropTypes.string,
  resource: PropTypes.string,
  translate: PropTypes.func,
  disableAdd: PropTypes.bool,
  disableRemove: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  perPage: PropTypes.number,
  collapsable: PropTypes.bool,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  TransitionProps: PropTypes.shape({}),
};

export default SimpleFormIterator;
